import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  User, Phone, Mail, MapPin, Calendar, ChevronLeft, Home, Plus,
  ClipboardCheck, FileText, Building2, Loader2, Clock, CheckCircle,
  XCircle, AlertCircle, Edit, ArrowRight
} from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

const ROLES_CONFIG = {
  seller: { label: "Seller", color: "bg-blue-100 text-blue-700" },
  buyer: { label: "Buyer", color: "bg-green-100 text-green-700" },
  landlord: { label: "Landlord", color: "bg-purple-100 text-purple-700" },
  tenant: { label: "Tenant", color: "bg-amber-100 text-amber-700" }
};

const VALUATION_STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  booked: { label: "Booked", color: "bg-blue-100 text-blue-700", icon: Calendar },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  instructed: { label: "Instructed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  lost: { label: "Lost", color: "bg-red-100 text-red-700", icon: XCircle }
};

const PROPERTY_TYPES = [
  { value: "detached", label: "Detached" },
  { value: "semi_detached", label: "Semi-Detached" },
  { value: "terraced", label: "Terraced" },
  { value: "flat", label: "Flat" },
  { value: "bungalow", label: "Bungalow" },
  { value: "maisonette", label: "Maisonette" },
  { value: "cottage", label: "Cottage" },
  { value: "townhouse", label: "Townhouse" }
];

export const ContactDetailPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [valuations, setValuations] = useState([]);
  const [listings, setListings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Valuation modal
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [valuationType, setValuationType] = useState("sales");
  const [savingValuation, setSavingValuation] = useState(false);
  const [valuationForm, setValuationForm] = useState({
    property_address_line1: "",
    property_address_line2: "",
    property_town: "",
    property_county: "",
    property_postcode: "",
    property_type: "",
    bedrooms: "",
    tenure: "freehold",
    reason_for_move: "",
    timescale: "",
    valuer_id: "",
    notes: "",
    questions_for_valuer: "",
    // Lettings specific
    furnished: "",
    current_situation: ""
  });

  // Book valuation modal
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    date: "",
    time: "",
    valuer_id: "",
    send_confirmation: true
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  const fetchContact = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/contacts/${contactId}`, { headers });
      setContact(res.data?.contact);
      setValuations(res.data?.valuations || []);
      setListings(res.data?.listings || []);
    } catch (error) {
      console.error("Error fetching contact:", error);
      toast.error("Failed to load contact");
    } finally {
      setLoading(false);
    }
  }, [token, contactId]);

  const fetchStaff = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/staff`, { headers });
      setStaffList(res.data?.staff || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchContact();
    fetchStaff();
  }, [fetchContact, fetchStaff]);

  const handleCreateValuation = async () => {
    if (!valuationForm.property_address_line1 || !valuationForm.property_postcode) {
      toast.error("Please enter property address and postcode");
      return;
    }

    setSavingValuation(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        contact_id: contactId,
        valuation_type: valuationType,
        ...valuationForm,
        bedrooms: valuationForm.bedrooms ? parseInt(valuationForm.bedrooms) : null
      };

      await axios.post(`${API}/contacts/valuations`, payload, { headers });
      toast.success("Valuation card created");
      setShowValuationModal(false);
      resetValuationForm();
      fetchContact();
    } catch (error) {
      console.error("Error creating valuation:", error);
      toast.error(error.response?.data?.detail || "Failed to create valuation");
    } finally {
      setSavingValuation(false);
    }
  };

  const handleBookValuation = async () => {
    if (!bookingForm.date || !bookingForm.time || !bookingForm.valuer_id) {
      toast.error("Please select date, time, and valuer");
      return;
    }

    setBookingLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/contacts/valuations/book`, {
        valuation_id: selectedValuation.valuation_id,
        ...bookingForm
      }, { headers });

      toast.success("Valuation booked successfully");
      setShowBookModal(false);
      setSelectedValuation(null);
      setBookingForm({ date: "", time: "", valuer_id: "", send_confirmation: true });
      fetchContact();
    } catch (error) {
      console.error("Error booking valuation:", error);
      toast.error(error.response?.data?.detail || "Failed to book valuation");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleValuationOutcome = async (valuationId, outcome) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/contacts/valuations/${valuationId}/outcome`, {
        valuation_id: valuationId,
        outcome
      }, { headers });

      toast.success(`Valuation marked as ${outcome}`);
      fetchContact();
    } catch (error) {
      console.error("Error updating valuation:", error);
      toast.error("Failed to update valuation");
    }
  };

  const resetValuationForm = () => {
    setValuationForm({
      property_address_line1: "", property_address_line2: "", property_town: "",
      property_county: "", property_postcode: "", property_type: "", bedrooms: "",
      tenure: "freehold", reason_for_move: "", timescale: "", valuer_id: "",
      notes: "", questions_for_valuer: "", furnished: "", current_situation: ""
    });
  };

  const openValuationModal = (type) => {
    setValuationType(type);
    // Pre-fill address from contact
    if (contact?.address) {
      setValuationForm({
        ...valuationForm,
        property_address_line1: contact.address.line1 || "",
        property_address_line2: contact.address.line2 || "",
        property_town: contact.address.town || "",
        property_county: contact.address.county || "",
        property_postcode: contact.address.postcode || ""
      });
    }
    setShowValuationModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Contact not found</p>
      </div>
    );
  }

  const canCreateSalesValuation = contact.roles?.includes("seller");
  const canCreateLettingsValuation = contact.roles?.includes("landlord");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate("/contacts")} className="mb-3">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Contacts
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-800">{contact.full_name}</h1>
                  <div className="flex gap-1">
                    {contact.roles?.map(role => {
                      const config = ROLES_CONFIG[role];
                      return (
                        <Badge key={role} className={config?.color || "bg-slate-100"}>
                          {config?.label || role}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                  {contact.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {contact.email}
                    </span>
                  )}
                  {contact.mobile && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {contact.mobile}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2">
              {canCreateSalesValuation && (
                <Button variant="outline" onClick={() => openValuationModal("sales")}>
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Book Sales Valuation
                </Button>
              )}
              {canCreateLettingsValuation && (
                <Button variant="outline" onClick={() => openValuationModal("lettings")}>
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Book Rental Valuation
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="valuations">Valuations ({valuations.length})</TabsTrigger>
            <TabsTrigger value="listings">Listings ({listings.length})</TabsTrigger>
            {contact.buyer_profile && <TabsTrigger value="buyer">Buyer Details</TabsTrigger>}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Contact Details Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Name</Label>
                      <p className="font-medium">{contact.full_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Branch</Label>
                      <p className="font-medium">{contact.branch || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Email</Label>
                      <p className="font-medium">{contact.email || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Mobile</Label>
                      <p className="font-medium">{contact.mobile || contact.phone || "-"}</p>
                    </div>
                  </div>
                  {contact.address?.line1 && (
                    <div>
                      <Label className="text-slate-500">Address</Label>
                      <p className="font-medium">
                        {contact.address.line1}
                        {contact.address.line2 && `, ${contact.address.line2}`}
                        {contact.address.town && `, ${contact.address.town}`}
                        {contact.address.postcode && ` ${contact.address.postcode}`}
                      </p>
                    </div>
                  )}
                  {contact.assigned_negotiator && (
                    <div>
                      <Label className="text-slate-500">Assigned Negotiator</Label>
                      <p className="font-medium">{contact.assigned_negotiator}</p>
                    </div>
                  )}
                  {contact.notes && (
                    <div>
                      <Label className="text-slate-500">Notes</Label>
                      <p className="text-sm text-slate-600">{contact.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Activity Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Activity Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-600">{valuations.length}</p>
                      <p className="text-sm text-blue-600">Valuations</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-600">{listings.length}</p>
                      <p className="text-sm text-emerald-600">Listings</p>
                    </div>
                  </div>
                  
                  {/* Recent valuations */}
                  {valuations.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-slate-500 mb-2">Recent Valuations</p>
                      {valuations.slice(0, 2).map(val => (
                        <div key={val.valuation_id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div>
                            <p className="font-medium text-sm">{val.property?.address_line1}</p>
                            <p className="text-xs text-slate-500">{val.valuation_type}</p>
                          </div>
                          <Badge className={VALUATION_STATUS_CONFIG[val.status]?.color}>
                            {VALUATION_STATUS_CONFIG[val.status]?.label}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Valuations Tab */}
          <TabsContent value="valuations">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Valuations</CardTitle>
                  <CardDescription>Track valuation appointments and outcomes</CardDescription>
                </div>
                <div className="flex gap-2">
                  {canCreateSalesValuation && (
                    <Button size="sm" variant="outline" onClick={() => openValuationModal("sales")}>
                      <Plus className="w-4 h-4 mr-1" />
                      Sales
                    </Button>
                  )}
                  {canCreateLettingsValuation && (
                    <Button size="sm" variant="outline" onClick={() => openValuationModal("lettings")}>
                      <Plus className="w-4 h-4 mr-1" />
                      Rental
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {valuations.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No valuations yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {valuations.map(val => {
                      const statusConfig = VALUATION_STATUS_CONFIG[val.status];
                      const StatusIcon = statusConfig?.icon || FileText;
                      
                      return (
                        <div key={val.valuation_id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg ${statusConfig?.color} flex items-center justify-center`}>
                                <StatusIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <h4 className="font-semibold">{val.property?.address_line1}</h4>
                                <p className="text-sm text-slate-500">{val.property?.postcode}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {val.valuation_type === "sales" ? "Sales" : "Lettings"}
                                  </Badge>
                                  <Badge className={statusConfig?.color}>
                                    {statusConfig?.label}
                                  </Badge>
                                </div>
                                {val.booked_date && (
                                  <p className="text-sm text-slate-500 mt-2 flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {val.booked_date} at {val.booked_time}
                                    {val.valuer_name && ` - ${val.valuer_name}`}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions based on status */}
                            <div className="flex gap-2">
                              {val.status === "draft" && (
                                <Button 
                                  size="sm" 
                                  onClick={() => { setSelectedValuation(val); setShowBookModal(true); }}
                                >
                                  Book Appointment
                                </Button>
                              )}
                              {val.status === "booked" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleValuationOutcome(val.valuation_id, "completed")}
                                  >
                                    Mark Completed
                                  </Button>
                                </>
                              )}
                              {val.status === "completed" && (
                                <>
                                  <Button 
                                    size="sm"
                                    onClick={() => handleValuationOutcome(val.valuation_id, "instructed")}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Instruct
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleValuationOutcome(val.valuation_id, "lost")}
                                  >
                                    Mark Lost
                                  </Button>
                                </>
                              )}
                              {val.status === "instructed" && !val.listing_id && (
                                <Button 
                                  size="sm"
                                  onClick={() => navigate(`/listings/new?contact=${contactId}&valuation=${val.valuation_id}&type=${val.valuation_type}`)}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Create Listing
                                </Button>
                              )}
                              {val.listing_id && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => navigate(`/listings/${val.listing_id}`)}
                                >
                                  <ArrowRight className="w-4 h-4 mr-1" />
                                  View Listing
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <CardTitle>Property Listings</CardTitle>
                <CardDescription>Properties listed by this contact</CardDescription>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Home className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No listings yet</p>
                    <p className="text-sm">Create a valuation first, then instruct to create a listing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {listings.map(listing => (
                      <div 
                        key={listing.listing_id} 
                        className="p-4 border rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/listings/${listing.listing_id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{listing.property?.full_address}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">
                                {listing.listing_type === "sales" ? "For Sale" : "To Let"}
                              </Badge>
                              <Badge>
                                {listing.status?.replace(/_/g, " ")}
                              </Badge>
                              {listing.price_display && (
                                <span className="text-emerald-600 font-semibold">{listing.price_display}</span>
                              )}
                              {listing.rent_display && (
                                <span className="text-purple-600 font-semibold">{listing.rent_display}</span>
                              )}
                            </div>
                          </div>
                          <ChevronLeft className="w-5 h-5 text-slate-400 rotate-180" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Buyer Details Tab */}
          {contact.buyer_profile && (
            <TabsContent value="buyer">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Buyer Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-slate-500">Position</Label>
                      <p className="font-semibold text-lg">
                        {contact.buyer_profile.position === "ftb" && "First Time Buyer"}
                        {contact.buyer_profile.position === "has_property" && "Has Property to Sell"}
                        {contact.buyer_profile.position === "sold_stc" && "Sold (STC)"}
                        {contact.buyer_profile.position === "nothing_to_sell" && "Nothing to Sell (Cash/Investor)"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Budget</Label>
                      <p className="font-semibold text-lg">
                        {contact.buyer_profile.budget_min && contact.buyer_profile.budget_max
                          ? `£${contact.buyer_profile.budget_min.toLocaleString()} - £${contact.buyer_profile.budget_max.toLocaleString()}`
                          : contact.buyer_profile.budget_max
                            ? `Up to £${contact.buyer_profile.budget_max.toLocaleString()}`
                            : "Not specified"
                        }
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Bedrooms</Label>
                      <p className="font-semibold text-lg">
                        {contact.buyer_profile.bedrooms_min && contact.buyer_profile.bedrooms_max
                          ? `${contact.buyer_profile.bedrooms_min} - ${contact.buyer_profile.bedrooms_max}`
                          : contact.buyer_profile.bedrooms_min
                            ? `${contact.buyer_profile.bedrooms_min}+`
                            : "Not specified"
                        }
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Areas</Label>
                      <p className="font-semibold">
                        {contact.buyer_profile.areas?.length > 0
                          ? contact.buyer_profile.areas.join(", ")
                          : "Any area"
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Create Valuation Modal */}
      <Dialog open={showValuationModal} onOpenChange={setShowValuationModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {valuationType === "sales" ? "Create Sales Valuation" : "Create Rental Valuation"}
            </DialogTitle>
            <DialogDescription>
              Create a valuation card for {contact.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              {/* Property Address */}
              <div>
                <Label className="font-semibold">Property Address</Label>
                <PostcodeLookup
                  onAddressSelect={(addr) => {
                    setValuationForm({
                      ...valuationForm,
                      property_address_line1: addr.line_1 + (addr.line_2 ? ", " + addr.line_2 : ""),
                      property_address_line2: addr.line_3 || "",
                      property_town: addr.town_or_city || "",
                      property_county: addr.county || "",
                      property_postcode: addr.postcode || ""
                    });
                  }}
                />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="col-span-2">
                    <Label>Address Line 1 *</Label>
                    <Input
                      value={valuationForm.property_address_line1}
                      onChange={(e) => setValuationForm({...valuationForm, property_address_line1: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Town</Label>
                    <Input
                      value={valuationForm.property_town}
                      onChange={(e) => setValuationForm({...valuationForm, property_town: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Postcode *</Label>
                    <Input
                      value={valuationForm.property_postcode}
                      onChange={(e) => setValuationForm({...valuationForm, property_postcode: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Property Type</Label>
                  <Select 
                    value={valuationForm.property_type}
                    onValueChange={(v) => setValuationForm({...valuationForm, property_type: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bedrooms</Label>
                  <Input
                    type="number"
                    value={valuationForm.bedrooms}
                    onChange={(e) => setValuationForm({...valuationForm, bedrooms: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Tenure</Label>
                  <Select 
                    value={valuationForm.tenure}
                    onValueChange={(v) => setValuationForm({...valuationForm, tenure: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freehold">Freehold</SelectItem>
                      <SelectItem value="leasehold">Leasehold</SelectItem>
                      <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lettings specific */}
              {valuationType === "lettings" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Furnished</Label>
                    <Select 
                      value={valuationForm.furnished}
                      onValueChange={(v) => setValuationForm({...valuationForm, furnished: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unfurnished">Unfurnished</SelectItem>
                        <SelectItem value="part_furnished">Part Furnished</SelectItem>
                        <SelectItem value="furnished">Furnished</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Current Situation</Label>
                    <Select 
                      value={valuationForm.current_situation}
                      onValueChange={(v) => setValuationForm({...valuationForm, current_situation: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="empty">Empty</SelectItem>
                        <SelectItem value="tenanted">Currently Tenanted</SelectItem>
                        <SelectItem value="notice_served">Notice Served</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Seller/Landlord details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Reason for {valuationType === "sales" ? "Selling" : "Letting"}</Label>
                  <Input
                    value={valuationForm.reason_for_move}
                    onChange={(e) => setValuationForm({...valuationForm, reason_for_move: e.target.value})}
                    className="mt-1"
                    placeholder="e.g., Relocating, Investment..."
                  />
                </div>
                <div>
                  <Label>Timescale</Label>
                  <Select 
                    value={valuationForm.timescale}
                    onValueChange={(v) => setValuationForm({...valuationForm, timescale: v})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-2_months">1-2 Months</SelectItem>
                      <SelectItem value="3+_months">3+ Months</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Valuer Assignment */}
              <div>
                <Label>Assign Valuer</Label>
                <Select 
                  value={valuationForm.valuer_id}
                  onValueChange={(v) => setValuationForm({...valuationForm, valuer_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select valuer" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map(staff => (
                      <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={valuationForm.notes}
                  onChange={(e) => setValuationForm({...valuationForm, notes: e.target.value})}
                  className="mt-1"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>

              <div>
                <Label>Questions for Valuer</Label>
                <Textarea
                  value={valuationForm.questions_for_valuer}
                  onChange={(e) => setValuationForm({...valuationForm, questions_for_valuer: e.target.value})}
                  className="mt-1"
                  rows={2}
                  placeholder="Questions you want the valuer to ask..."
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowValuationModal(false); resetValuationForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateValuation} disabled={savingValuation}>
              {savingValuation && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Valuation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Valuation Modal */}
      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Book Valuation Appointment</DialogTitle>
            <DialogDescription>
              {selectedValuation?.property?.address_line1}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={bookingForm.date}
                onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Time *</Label>
              <Input
                type="time"
                value={bookingForm.time}
                onChange={(e) => setBookingForm({...bookingForm, time: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Valuer *</Label>
              <Select 
                value={bookingForm.valuer_id}
                onValueChange={(v) => setBookingForm({...bookingForm, valuer_id: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select valuer" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map(staff => (
                    <SelectItem key={staff.user_id} value={staff.user_id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send_confirmation"
                checked={bookingForm.send_confirmation}
                onChange={(e) => setBookingForm({...bookingForm, send_confirmation: e.target.checked})}
              />
              <Label htmlFor="send_confirmation" className="cursor-pointer">
                Send confirmation email to {contact.full_name}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBookModal(false); setSelectedValuation(null); }}>
              Cancel
            </Button>
            <Button onClick={handleBookValuation} disabled={bookingLoading}>
              {bookingLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Book Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactDetailPage;
